/**
 * Near-Me Lead Schemas
 * ====================
 * Direct-lead model: a seeker sends ONE enquiry / quote / callback / etc. to
 * ONE business (the owner sees it in their plan-gated Leads inbox). Create is
 * public (name + phone captured); listing/updating is owner-scoped.
 */

import { z } from 'zod';
import type { NearMeLeadStatus, NearMeLeadType } from './enums.js';
import { NEAR_ME_LEAD_STATUS, NEAR_ME_LEAD_TYPE } from './enums.js';
import { phoneSchema } from './helpers.js';

const objectIdSchema = z.string().regex(/^[a-f\d]{24}$/i, 'Invalid id');

/** Create a lead (public — the seeker leaves their contact). */
export const createLeadBodySchema = z.object({
  businessId: objectIdSchema,
  type: z.enum(NEAR_ME_LEAD_TYPE),
  name: z.string().trim().min(2).max(80),
  phone: phoneSchema,
  message: z.preprocess(
    (v) => (typeof v === 'string' && v.trim() === '' ? undefined : v),
    z.string().trim().max(500).optional(),
  ),
  serviceInterested: z.preprocess(
    (v) => (typeof v === 'string' && v.trim() === '' ? undefined : v),
    z.string().trim().max(120).optional(),
  ),
});
export type CreateLeadInput = z.infer<typeof createLeadBodySchema>;

/** Statuses an owner can set (everything except the system-initial `new`). */
export const OWNER_LEAD_STATUSES = ['viewed', 'contacted', 'converted', 'closed', 'spam'] as const;

export const updateLeadStatusBodySchema = z.object({
  status: z.enum(OWNER_LEAD_STATUSES),
});
export type UpdateLeadStatusInput = z.infer<typeof updateLeadStatusBodySchema>;

/** Owner's leads inbox query. */
export const myLeadsQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(50).default(20),
  status: z.enum(NEAR_ME_LEAD_STATUS).optional(),
  businessId: objectIdSchema.optional(),
});
export type MyLeadsQuery = z.infer<typeof myLeadsQuerySchema>;

// ─── Wire types ──────────────────────────────────────────────────────────────

export interface NearMeLead {
  _id: string;
  businessId: string;
  businessName?: string;
  ownerId: string;
  type: NearMeLeadType;
  name: string;
  phone: string;
  message?: string;
  serviceInterested?: string;
  status: NearMeLeadStatus;
  source: string;
  seekerUserId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface NearMeLeadListResult {
  items: NearMeLead[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}
